<template>
  <list-layout :tableTools="toggleTableTools" >
    <template slot="header">
      <BaseHeader :content="header" />
    </template>
    <template slot="table-core">
      <base-table-pagination
        :total="pagination.total"
        :currentPage="pagination.currentPage"
        :perPage="pagination.pageSize"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      ></base-table-pagination>
      <div class="flex flex-row w-100">
        <base-table-searches @toggleSearchesDialog="toggleSearchesDialog" />
        <base-table-search @toggleFilters="toggleFilters" />
      </div>
    </template>
    <template slot="table-tags">
      <base-table-filter-tags :tags="tags" @removeTag="removeFilterTagHanlder" @removeAllTags="removeAllTagsHandler" @saveTags="saveTagsHandler"/>
    </template>
    <template slot="table-component">
      <kendo-grid-component
        :value="value"
        :value-key="valueKey"
        :include-key="includeKey"
        :value-type="valueType"
        :id-vista="idVista"
        :selected-field="selectedField"
        :in-edit-field="inEditField"
        :expand-field="expandField"
        :single="single"
        :select-on-row-click="selectOnRowClick"
        :edit-on-row-click="editOnRowClick"
        :expand-on-row-click="expandOnRowClick"
        :is-row-editable="isRowEditable"
        :update-columns="updateColumns"
        :spanish="spanish"
        :data="data"
        :kendo="kendo"
        :loading="isGeneralLoading"
        @input="$emit('input', $event)"
        @actionclick="openPath"
        @datastatechange='dataStateChange'
        v-on="$listeners">
      </kendo-grid-component>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive v-if="showBackButton && headerUrl" mode="secondary" :text="$t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>
          <base-button-responsive v-if="!hideExcelButton" mode="primary" :text="labelBtnExcel" icon="excelxls" @click="exportExcel" :loading="isGeneralLoading || loadingExcel" :disabled="loadingExcel"/>
          <base-table-select-actions-form 
            v-if="actionsWithSelect"
            :name="nameSelectActions"
            :baseDropDownVisible="footerAcciones !== null && footerAcciones.length > 0">
            <template slot="more">
              <span v-for="action in footerAcciones" :key="action.IdAccion">
                <base-button-item-list theme="dark" @click="action.handler" :disabled="!!action.disabled">
                  <base-icon :name="action.icon" class="mr-2" />
                  <div class="w-72 text-left">
                    {{ action.text }}
                  </div>
                </base-button-item-list>
              </span>
            </template>
          </base-table-select-actions-form>
          <div class="flex items-center space-x-4 md:space-x-2" v-else>
            <base-button-responsive v-for="(action, index) in footerAcciones" :key="index" mode="primary" 
              :text="action.text" :icon="action.icon" :disabled="!!action.disabled" v-show="!action.hide"
              :loading="isGeneralLoading" @click="action.handler" />
          </div>
        </template>
      </base-footer>
      <base-table-select-actions-expand
        v-if="!hideTableActions"
        :baseDropDownVisible="hasAccionesPosibles"
        :visibility="hasSelected"
        :selected="rowsSelected"
        :total="rowsTotal"
      >
        <template slot="commons">
          <base-button size="xs" mode="tools" @click="unselect">
            <base-icon name="vall" size="sm" class="mr-1" />
            {{$t('app.unselect')}}
          </base-button>
        </template>
        <template slot="more">
          <span v-for="action in getAccionesPosibles" :key="action.IdAccion">
            <base-button-item-list theme="dark" @click="executeAction({ action, event: $event })">
              <base-icon v-if="action.icon" :name="action.icon" class="mr-2" />
                {{ action.Descripcion }}
            </base-button-item-list>
          </span>
        </template>
      </base-table-select-actions-expand>
      <slot name="dialogs" />
      <component
        :is="FilterPanel"
        :panelOpen="filterPanel" @togglePanel="toggleFilters" @applyFilters="applyFiltersHandler" 
        :vuexnamespace="vuexnamespace"
        />
      <create-search-dialog 
        :numberSearches="numberSearches"
        :idVista="idVista" 
        :dialogOpen="saveSearchDialog" 
        :tags="tags" 
        @toggleDialog="toggleDialogSearches" 
        @applyDialog="applySearchHandler" />
      <filter-panel-searches 
        ref="filterPanelSearches" 
        :idVista="idVista" 
        :vuexnamespace="vuexnamespace" 
        :panelOpen="searchesPanel" 
        @togglePanel="toggleSearchesDialog" 
        @applyFilters="applyFiltersHandler" 
        @updateNumberSearches="updateNumberSearches"/>
    </template>
  </list-layout>
</template>

<script>
import { i18n } from '@/i18n'
import getNestedValue from '@/services/functions/getNestedValue'
import getValueKeyOrDefault from '@/services/functions/getValueKeyOrDefault'
import waitUntil from '@/services/functions/waitUntil'
import ListLayout from '@/components/layout/ListLayout.vue'
import FilterPanelSearches from '@/components/searches/SearchesPanel.vue'
import KendoGridComponent from '@/components/KendoGridComponentV2.vue'
import { Dialog } from 'element-ui'
import CreateSearchDialog from '@/components/searches/CreateSearchDialog.vue'
import { isNil } from 'lodash'

import BreadCrumb from '@/classes/breadcrumb/BreadCrumb'

var cachedEntity = null

export default {
  name: 'table-list-view',
  components: {
    ListLayout,
    FilterPanelSearches,
    KendoGridComponent,
    CreateSearchDialog,
    Dialog,
  },
  props: {
    // #region KendoGridComponent properties
    value: {
      required: true
    },
    valueType: String,
    valueKey: {
      type: [Array, String, Number],
      required: true,
    },
    includeKey: [Array, String, Number],
    selectedField: {
      type: String,
      default: 'selected'
    },
    inEditField: {
      type: String,
      default: 'inEdit'
    },
    expandField: {
      type: String,
      default: 'expanded'
    },
    loading: {
      type: Boolean,
      default: false
    },
    single: {
      type: Boolean,
      default: false
    },
    selectOnRowClick: {
      type: Boolean,
      default: false
    },
    editOnRowClick: {
      type: Boolean,
      default: false
    },
    expandOnRowClick: {
      type: Boolean,
      default: false
    },
    isRowEditable: {
      type: Function,
      default: dataItem => true
    },
    updateColumns: {
      type: Boolean,
      default: false
    },
    // #endregion
    // #region GetData Functions
    getDetalle: {
      type: Function,
    },
    getPag: {
      type: Function,
      required: true,
    },
    getExcel: {
      type: Function,
      required: true,
    },
    // #endregion
    // #region GetParams Functions
    getParams: {
      type: Function,
      required: true,
    },
    getParamsMultiple: {
      type: Function,
      default: data => ({})
    },
    getParamsDetalle: {
      type: Function,
      default: data => ({})
    },
    getParamsPath: {
      type: Function,
      default: data => ({})
    },
    getParamsData: {
      type: Function,
      default: data => ({})
    },
    // #endregion
    // #region DataManagement Props&Functions
    disableRoutes: {
      type: Function,
    },
    normalizeData: {
      type: Function,
    },
    pagResponsePath: {
      type: String,
      default: ''
    },
    detailResponsePath: {
      type: String,
      default: ''
    },
    // #endregion
    // #region TableView properties
    FilterPanel: null,
    routeName: String,
    excelName: String,
    idVista: Number,
    headerTitle: String,
    headerIcon: String,
    headerUrl: String,
    vuexnamespace: String,
    breadcrumbName: String,
    breadcrumbs: {
      type: Array(BreadCrumb),
      default: () => []
    },
    listaAcciones: {
      type: Array,
      default: () => []
    },
    footerAcciones: {
      type: [],
      default: () => []
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    hideExcelButton: {
      type: Boolean,
      default: false
    },
    hideTableActions: {
      type: Boolean,
      default: false
    },
    actionsWithSelect: {
      type: Boolean,
      default: false
    },
    nameSelectActions: String,
    // #endregion
  },
  data: function () {
    return {
      // Searches
      numberSearches: 0,
      saveSearchDialog: false,
      searchesPanel: false,
      // All
      loadingExcel: false,
      spanish: true,
      hasClientFilterData: false,
      filterPanel: false,
      showAllRows: true,
      loadingDataTable: false,
      data: null,
      // selected: [],
      localBreadcrumbs: [],
      loadingEntity: false
    }
  },
  computed: {
    header: function () {
      return {
        title: this.headerTitle,
        icon: this.headerIcon,
        breadcrumb: this.localBreadcrumbs,
        url: this.headerUrl,
        params: this.$route.params
      }
    },
    isClientFilterActive () {
      return this.$store.getters[this.vuexnamespace + '/isClientFilterActive']
    },
    actualFilterNav () {
      return this.$store.getters['app/idClienteFilterNav']
    },
    labelBtnExcel: function () {
      if (this.hasSelected) return `${i18n.t('app.export_excel')} (${i18n.t('app.selected')})`
      return i18n.t('app.export_excel')
    },
    kendo: function () {
      return this.$store.getters[this.vuexnamespace + '/getKendo']
    },
    isHeadCoreReady: function () {
      return this.$store.getters.isHeadCoreReady
    },
    selected: function () {
      if (this.single) {
        return !isNil(this.value) ? [this.value] : []
      }
      return this.value
    },
    toggleTableTools: function () {
      return this.hasSelected
    },
    rowsSelected: function () {
      return this.selected.length
    },
    rowsTotal: function () {
      return this.data?.length ?? 0
    },
    setLabelTowShow: function () {
      return this.showAllRows ? i18n.t('app.only_selected') : i18n.t('app.see_all')
    },
    masterFilters: function () {
      return this.$store.getters[this.vuexnamespace + '/getMasterFilters']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading || this.loading
    },
    headerCoreFilters: function () {
      return this.$store.getters['auth/queryHeaders']
    },
    tags: function () {
      return this.$store.getters[this.vuexnamespace + '/getTags']
    },
    pagination: function () {
      return this.$store.getters[this.vuexnamespace + '/getPaginationInfo']
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    getAccionesPosibles () {
      return this.listaAcciones
    },
    hasAccionesPosibles () {
      return this.getAccionesPosibles.length > 0
    }
  },
  watch: {
    $route (to, from) {
      this.updateOnRouteChange(to, from)
    },
    headerCoreFilters: {
      // immediate: true,
      deep: true,
      handler: 'headerCoreFiltersHandler'
    },
    breadcrumbs: {
      deep: true,
      handler: function (value) {
        this.updateBreadcrumbs(value, null)
      }
    },
  },
  created: async function () {
    if (this.$route.params.data) {
      this.filterByParamsData()
    }
  },
  mounted: async function () {
    this.$store.commit(this.vuexnamespace + '/setPageSize', this.kendo.take)

    var entity = null
    if (typeof this.getDetalle === 'function' && this.disableRoutes) {
      entity = await this.fetchEntity()
      this.disableRoutes(entity)
    }
    this.updateBreadcrumbs(this.breadcrumbs, entity)
    

    this.data = await this.applyFilters()
  },
  methods: {
    async waitUntilReady () {
      await waitUntil(() => !this.loadingEntity, 500)
    },
    updateBreadcrumbs: async function (breadcrumbsData = [], entity = null) {
      var needRoute = false
      if (breadcrumbsData?.length && (typeof this.getDetalle === 'function' || (breadcrumbsData.every(x => x?.name) && !breadcrumbsData.at(-1).forceDynamic))) {
        const breadcrumbs = []
        const data = breadcrumbsData.map(x => Object.assign(new BreadCrumb(), x))
        for (const key in data) {
          var routeData = null
          const breadcrumb = data[key]
          if (key === (data.length - 1).toString()) {
            breadcrumb.getter = breadcrumb.getter || 'app/headerBreadCrumb'
            breadcrumb.setter = breadcrumb.setter || 'app/setheaderBreadCrumb'
            breadcrumb.url = ''
            if (breadcrumb.forceDynamic) {
              breadcrumb.paths = breadcrumb.paths || [[breadcrumb.name]]
              breadcrumb.name = ''
            }
          }
          if (this.$store.getters['app/fromRouteName'] !== null && breadcrumb.getter) {
            routeData = this.$store.getters[breadcrumb.getter]
          } else {
            if (!entity && !breadcrumb.name && (breadcrumb.paths?.length || breadcrumb.dataCallback?.name)) {
              entity = await this.fetchEntity()
            }
            routeData = breadcrumb.dataCallback(entity, this, breadcrumb)
            if (breadcrumb.setter) {
              needRoute ||= !!entity
              this.$store.commit(breadcrumb.setter, routeData)
            }
          }

          breadcrumbs.push(breadcrumb.routeCallback(routeData, this, breadcrumb))
        }
        if (needRoute) {
          this.$store.commit('app/setfromRoute', { name: this.$route.name, params: this.$route.params, query: this.$route.query })
        }
        this.localBreadcrumbs = breadcrumbs
      }
    },
    fetchEntity: async function () {
      if (cachedEntity) {
        return cachedEntity
      }
      if (this.loadingEntity) {
        await this.waitUntilReady()
        return cachedEntity
      }
      this.loadingEntity = true
      cachedEntity = await this.downloadData(this.getParamsDetalle())
      this.loadingEntity = false
      return cachedEntity
    },
    filterByParamsData: function () {
      const filters = this.masterFilters
      if (this.actualFilterNav && this.isClientFilterActive && !this.hasClientFilterData) {
        if (this.vuexnamespace !== 'distributors') {
          filters.empresa = { label: i18n.t('clients.entity_name'), value: this.actualFilterNav }
        } 
      }

      const data = this.$route.params.data
      if (data) {
        data.filters.forEach(filter => {
          if (filter.type === 'cliente') {
            this.hasClientFilterData = true
            const cliente = { label: i18n.t('clients.entity_name'), value: { Nombre: filter.data.label, idCliente: filter.data.id } }
            filters.empresa = cliente
          } else {
            filters[filter.type] = filter.data
          }
        })
        this.$store.commit(this.vuexnamespace + '/setMasterFilters', filters)
      }
    },
    dataStateChange: async function (event) {
      this.data = await this.applyFilters()
    },
    goBack: function () {
      if (this.headerUrl) {
        this.$router.push({ name: this.headerUrl, params: this.$route.params })
      }
    },
    openPath: function (event) {
      const breadcrumb = event.dataItem[this.breadcrumbName]
      const params = this.getParamsPath(event.dataItem)
      this.$store.commit('app/setheaderBreadCrumb', breadcrumb)
      this.$router.push({ name: this.routeName, params })
    },
    toggleFilters: function () {
      this.filterPanel = !this.filterPanel
    },
    toggleSearchesDialog: function () {
      this.searchesPanel = !this.searchesPanel
    },
    removeFilterTag: function (tag) {
      this.tags.splice(this.tags.indexOf(tag), 1)
    },
    exportExcel: async function () {
      this.$store.commit('app/setLoading', true)
      this.loadingExcel = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = { ...this.getParams(data) }

      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      try {
        const objColumnas = this.getColumnsGroupExcelMixin(this.kendo.columns, this.kendo.group)
        const payload = { order: objOrder.order, grupos: objColumnas, ...this.getParamsMultiple(data) }
        if (this.hasSelected) {
          var mapper = x => getValueKeyOrDefault(x, this.valueKey, { asObject: true })
          const selectedIDs = this.selected.map(mapper)
          payload.listaIDs = selectedIDs
        }
        const response = await this.getExcel(payload, params)
        const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        let filename = this.excelName
        filename = decodeURI(filename)
        link.download = filename
        link.click()
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          console.error(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('app/setLoading', false)
        this.loadingExcel = false
      }
    },
    expandChange: function (event) {
      event.dataItem[event.target.$props.expandField] = !event.dataItem[event.target.$props.expandField]
    },
    updateOnRouteChange: async function (to, from) {
      this.filterByParamsData()
      await this.applyFilters()
    },
    applyFilters: async function (eventData) {
      this.loadingDataTable = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      this.$store.commit(this.vuexnamespace + '/setClientFilterActive', !!((data.empresa && data.empresa.value.idCliente === this.actualFilterNav?.idCliente) || (this.hasClientFilterData)))
      const params = { ...this.getParams(data) }
      const objOrder = { ...this.getParamsMultiple(data), ...this.getObjOrderMixin(this.kendo.group, this.kendo.sort) }
      try {
        const res = await this.getItemsData(objOrder, params)
        this.data = typeof this.normalizeData === 'function' ? this.normalizeData(res) : res
      } catch (error) {
        this.$store.commit('setIsLoading', false)
        this.doFeedback()
      }
      return this.data
    },
    getItemsData: async function (objOrder, filters) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const res = await this.getMainData(objOrder, params)
      const response = (this.pagResponsePath ? getNestedValue(this.pagResponsePath, res) : (res.Respuesta || res.respuesta)) || []
      this.data = [response].flat()
      const pagination = {
        total: res.meta?.total,
        currentPage: res.meta?.currentPage,
        totalPages: res.meta?.lastPage,
        pageSize: res.meta?.perPage
      }
      this.$store.commit(this.vuexnamespace + '/setPaginationInfo', pagination)
      this.$store.commit('setIsLoading', false)
      this.loadingDataTable = false
      return this.data
    },
    async getMainData (objOrder, filters) {
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const data = await this.getPag(objOrder, params)
      if (data.blnError === true) {
        this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data))
      }
      return data
    },
    applyFiltersHandler: async function (eventData) {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.data = await this.applyFilters(eventData)
    },
    changeCurrentPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', event)
      this.data = await this.applyFilters(null)
    },
    changePerPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/setPageSize', event)
      this.kendo.take = event
      this.data = await this.applyFilters(null)
    },
    removeFilterTagHanlder: function (tag) {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      const filter = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      delete filter[tag[0]]
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      this.applyFiltersHandler(null)
    },
    headerCoreFiltersHandler: function (obs) {
      if (this.isHeadCoreReady) {
        this.applyFilters(null)
      }
    },
    removeAllTagsHandler: function () {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
      this.applyFiltersHandler(null)
    },
    tableSortByChangeHandler: function ({ prop, order }) {
      this.$store.commit(this.vuexnamespace + '/setSort', prop)
      this.$store.commit(this.vuexnamespace + '/setSortOrder', order)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.applyFilters(null)
    },
    downloadData: async function (params) {
      try {
        this.$store.commit('setIsLoading', true)
        const data = await this.getDetalle(params)
        if (data.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data))
        } else {
          return (this.detailResponsePath ? getNestedValue(this.detailResponsePath, data) : (data.Respuesta || data.respuesta)) || null
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      return null
    },
    unselect: function () {
      this.$emit('input', [])
    },
    executeAction: async function ({ action, event }) {
      this.$emit('executeAction', { action, selected: this.selected, event })
    },
    saveTagsHandler: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    toggleDialogSearches: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    applySearchHandler: async function () {
      this.toggleDialogSearches()
      // Manda actualizar lista de búsquedas
      this.$refs.filterPanelSearches.updateData()
    },
    updateNumberSearches: async function (number) {
      this.numberSearches = number
    },
  },
  beforeDestroy () {
    cachedEntity = null
  },
}
</script>
